import { useEffect, useState, lazy, Suspense } from "react";
import { Link, NavLink, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  LayoutDashboard, Plus, MapPin, User, Wallet, Bell,
  LogOut, Crown, Store, Shield, Trophy, Search, Command as CommandIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import BrandMark from "@/components/brand/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import { cn } from "@/lib/utils";

const CommandBar = lazy(() => import("@/components/nav/CommandBar"));

type NavItem = { icon: typeof LayoutDashboard; label: string; path: string };

const AppLayout = () => {
  const { user, profile, isVendor, isAdmin, signOut } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [hoverRail, setHoverRail] = useState(false);
  const [dockHidden, setDockHidden] = useState(false);

  const primaryNav: NavItem[] = [
    { icon: LayoutDashboard, label: "Home", path: "/dashboard" },
    { icon: MapPin, label: "Venues", path: "/venues" },
    { icon: Plus, label: "Create", path: "/create-krew" },
    { icon: Trophy, label: "Ranks", path: "/leaderboard" },
    { icon: Wallet, label: "Wallet", path: "/wallet" },
  ];

  const railNav: NavItem[] = [
    ...primaryNav,
    { icon: User, label: "Profile", path: "/profile" },
    { icon: Bell, label: "Inbox", path: "/notifications" },
    ...(isVendor ? [{ icon: Store, label: "Vendor", path: "/vendor" }] : []),
    ...(isAdmin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  // ⌘K toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Hide mobile dock on scroll-down, reveal on scroll-up
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y > lastY + 6 && y > 80) setDockHidden(true);
        else if (y < lastY - 6) setDockHidden(false);
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      const { count } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
      setUnreadCount(count || 0);
    };
    fetchUnread();
    const channel = supabase
      .channel("notif-badge")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => fetchUnread())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const initials = (profile?.display_name?.[0] ?? "?").toUpperCase();
  const reliability = profile?.reliability_score ?? 100;

  return (
    <div className="relative flex min-h-screen text-foreground">
      {/* Ambient blobs — only visible on app routes */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-krew-violet/10 blur-3xl animate-blob-float" />
        <div className="absolute top-1/3 -right-24 h-[460px] w-[460px] rounded-full bg-krew-cyan/10 blur-3xl animate-blob-float [animation-delay:-6s]" />
        <div className="absolute bottom-0 left-1/3 h-[380px] w-[380px] rounded-full bg-krew-magenta/[0.08] blur-3xl animate-blob-float [animation-delay:-12s]" />
      </div>

      {/* DESKTOP RAIL — expands on hover */}
      <motion.aside
        onMouseEnter={() => setHoverRail(true)}
        onMouseLeave={() => setHoverRail(false)}
        animate={{ width: hoverRail ? 240 : 76 }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        className="fixed inset-y-0 left-0 z-40 hidden flex-col p-3 lg:flex"
      >
        <div className="relative flex h-full flex-col rounded-3xl bg-glass shadow-soft overflow-hidden">
          {/* Brand */}
          <div className="flex h-16 items-center gap-3 px-4">
            <BrandMark size={32} animate={false} />
            <AnimatePresence>
              {hoverRail && (
                <motion.span
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  className="font-display text-xl font-semibold tracking-tight text-gradient-ribbon animate-ribbon-shift"
                >
                  krew
                </motion.span>
              )}
            </AnimatePresence>
            {profile?.is_premium && <Crown className="ml-auto h-4 w-4 text-krew-sunburst" />}
          </div>

          {/* ⌘K trigger */}
          <button
            onClick={() => setCmdOpen(true)}
            className="mx-3 mb-2 flex items-center gap-2 rounded-2xl bg-muted/40 px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/70"
          >
            <Search className="h-4 w-4 shrink-0" />
            <AnimatePresence>
              {hoverRail && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex-1 truncate"
                >
                  Quick find
                </motion.span>
              )}
            </AnimatePresence>
            {hoverRail && (
              <kbd className="rounded-md border border-border bg-background/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">⌘K</kbd>
            )}
          </button>

          {/* Nav items */}
          <nav className="relative flex-1 space-y-1 px-2">
            {railNav.map((item) => {
              const active = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "relative flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-medium transition-colors",
                    active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {active && (
                    <motion.span
                      layoutId="nav-indicator-rail"
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                      className="absolute inset-0 rounded-2xl bg-ribbon-soft ring-1 ring-foreground/[0.08]"
                    />
                  )}
                  <span className="relative z-[1] flex h-5 w-5 items-center justify-center">
                    <item.icon className="h-5 w-5" />
                    {item.path === "/notifications" && unreadCount > 0 && (
                      <span className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-ribbon font-mono text-[9px] font-bold text-krew-ink">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </span>
                  <AnimatePresence>
                    {hoverRail && (
                      <motion.span
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -6 }}
                        className="relative z-[1] whitespace-nowrap"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </NavLink>
              );
            })}
          </nav>

          {/* Profile footer */}
          <div className="border-t border-border/50 p-3">
            <Link to="/profile" className="flex items-center gap-3 rounded-2xl p-2 transition-colors hover:bg-muted/40">
              <span className="relative inline-flex h-10 w-10 items-center justify-center">
                <span className="absolute inset-0 rounded-full bg-conic-ribbon" style={{ mask: "radial-gradient(closest-side, transparent 64%, #000 66%)", WebkitMask: "radial-gradient(closest-side, transparent 64%, #000 66%)" }} />
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-card font-mono text-sm font-bold text-foreground">
                    {initials}
                  </span>
                )}
              </span>
              <AnimatePresence>
                {hoverRail && (
                  <motion.span
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="min-w-0 flex-1 truncate"
                  >
                    <span className="block truncate text-sm font-medium">{profile?.display_name ?? "You"}</span>
                    <span className="block font-mono text-[10px] text-muted-foreground">REL · {reliability}</span>
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
            <AnimatePresence>
              {hoverRail && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 flex items-center gap-2 overflow-hidden"
                >
                  <ThemeToggle />
                  <button onClick={signOut} className="ml-auto inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.aside>

      {/* MOBILE TOP BAR */}
      <div className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between px-4 lg:hidden">
        <div className="absolute inset-0 -z-10 bg-background/60 backdrop-blur-xl" />
        <BrandMark size={28} withWordmark animate={false} />
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCmdOpen(true)}
            aria-label="Search"
            className="rounded-full p-2 text-muted-foreground hover:text-foreground"
          >
            <Search className="h-5 w-5" />
          </button>
          <Link to="/notifications" className="relative rounded-full p-2 text-muted-foreground hover:text-foreground">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-ribbon" />
            )}
          </Link>
          <Link to="/profile" className="relative ml-1 inline-flex h-9 w-9 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-conic-ribbon" style={{ mask: "radial-gradient(closest-side, transparent 64%, #000 66%)", WebkitMask: "radial-gradient(closest-side, transparent 64%, #000 66%)" }} />
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
            ) : (
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-card font-mono text-xs font-bold">
                {initials}
              </span>
            )}
          </Link>
        </div>
      </div>

      {/* MAIN */}
      <main className="relative flex-1 pb-32 pt-16 lg:ml-[88px] lg:pb-12 lg:pt-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}
            className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* MOBILE DOCK */}
      <motion.nav
        animate={{ y: dockHidden ? 120 : 0, opacity: dockHidden ? 0 : 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        className="fixed inset-x-0 bottom-3 z-40 mx-auto flex max-w-md items-center justify-around px-3 lg:hidden"
      >
        <div className="relative flex w-full items-center justify-around rounded-pill bg-glass px-2 py-1.5 shadow-dock">
          {primaryNav.map((item) => {
            const active = location.pathname === item.path;
            const isCenter = item.path === "/create-krew";
            if (isCenter) {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="relative -my-4 flex h-14 w-14 items-center justify-center rounded-full bg-ribbon bg-[length:200%_200%] animate-ribbon-shift shadow-ribbon ribbon-sweep"
                  aria-label="Create Krew"
                >
                  <Plus className="h-6 w-6 text-krew-ink" strokeWidth={2.5} />
                </Link>
              );
            }
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  "relative flex flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-2 py-2 text-[10px] font-medium transition-colors",
                  active ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {active && (
                  <motion.span
                    layoutId="nav-indicator-dock"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    className="absolute inset-0 rounded-full bg-ribbon-soft ring-1 ring-foreground/[0.08]"
                  />
                )}
                <item.icon className="relative z-[1] h-5 w-5" />
                <span className="relative z-[1] tracking-wide">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </motion.nav>

      {/* COMMAND BAR */}
      <Suspense fallback={null}>
        {cmdOpen && <CommandBar open={cmdOpen} onOpenChange={setCmdOpen} extraItems={railNav} />}
      </Suspense>
    </div>
  );
};

export default AppLayout;